import React, { useMemo } from 'react';
import { Box, Grid, Slider, Typography, Chip } from '@mui/material';
import { PartnershipScenario, CalculatedResults } from '../types/types';
import { calculateROI } from '../utils/calculations';

interface SensitivityAnalysisProps {
  scenario: PartnershipScenario;
  baselineResults: CalculatedResults;
}

const formatPct = (n: number) => `${n.toFixed(1)}%`;

const SensitivityAnalysis: React.FC<SensitivityAnalysisProps> = ({ scenario, baselineResults }) => {
  const [conv, setConv] = React.useState(0);
  const [deal, setDeal] = React.useState(0);
  const [cycle, setCycle] = React.useState(0);
  const [acq, setAcq] = React.useState(0);

  const adjustedScenario = useMemo(() => {
    const base = scenario;
    const newConv = base.returnMetrics.leadConversionRate * (1 + conv / 100);
    const newDeal = base.returnMetrics.averageDealSize * (1 + deal / 100);
    const newCycleLen = (base.returnMetrics.salesCycleLengthMonths || 3) * (1 + cycle / 100);
    const newAcq = (base.investmentMetrics.partnerAcquisitionCosts || 0) * (1 + acq / 100);
    return {
      ...base,
      returnMetrics: {
        ...base.returnMetrics,
        leadConversionRate: Math.max(0, newConv),
        averageDealSize: Math.max(0, newDeal),
        salesCycleLengthMonths: Math.max(1, Math.round(newCycleLen)),
      },
              investmentMetrics: {
            ...base.investmentMetrics,
            partnerAcquisitionCosts: Math.max(0, Math.round(newAcq)),
        },
    } as PartnershipScenario;
  }, [scenario, conv, deal, cycle, acq]);

  const adjusted = useMemo(() => calculateROI(adjustedScenario), [adjustedScenario]);
  const delta = adjusted.baseROIPercentage - baselineResults.baseROIPercentage;
  const isPositive = delta >= 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" aria-label="Sensitivity Analysis">Sensitivity Analysis</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={`Base ROI ${formatPct(baselineResults.baseROIPercentage)}`} />
          <Chip
            label={`Current ROI ${formatPct(adjusted.baseROIPercentage)}`}
            color={isPositive ? 'success' : 'error'}
            variant="outlined"
          />
          <Chip
            label={`Δ ${formatPct(delta)}`}
            color={isPositive ? 'success' : 'error'}
            variant="filled"
          />
        </Box>
      </Box>
      <Box role="status" aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
        ROI changed to {adjusted.baseROIPercentage.toFixed(1)} percent. Delta {delta.toFixed(1)} percent.
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">Deal conversion rate ({formatPct(conv)})</Typography>
          <Slider value={conv} onChange={(_, v) => setConv(v as number)} min={-50} max={100} step={1} aria-label="Deal conversion rate sensitivity"/>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">Average deal size ({formatPct(deal)})</Typography>
          <Slider value={deal} onChange={(_, v) => setDeal(v as number)} min={-30} max={50} step={1} aria-label="Average deal size sensitivity"/>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">Sales cycle length ({formatPct(cycle)})</Typography>
          <Slider value={cycle} onChange={(_, v) => setCycle(v as number)} min={-40} max={60} step={1} aria-label="Sales cycle sensitivity"/>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">Partner acquisition cost ({formatPct(acq)})</Typography>
          <Slider value={acq} onChange={(_, v) => setAcq(v as number)} min={-25} max={75} step={1} aria-label="Partner acquisition cost sensitivity"/>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SensitivityAnalysis;


